// MARK://dhk8/admin-shell/components/ConfirmDialog
// Reemplazo de window.confirm: <ConfirmProvider> + useConfirm() → confirm(opts):
// Promise<boolean>. Diálogo centrado sobre scrim (--pos-overlay), card redondeada
// (radio xl). Esc o click-fuera = cancelar (resuelve false); el foco arranca en el
// botón Confirmar. tone 'danger' tiñe el CTA en rojo. Sin edge-strips.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

export interface ConfirmOptions {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'default'
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve })
    })
  }, [])

  const close = useCallback((result: boolean) => {
    setPending((cur) => {
      cur?.resolve(result)
      return null
    })
  }, [])

  // Foco inicial en Confirmar + Esc para cancelar mientras hay diálogo abierto.
  useEffect(() => {
    if (!pending) return
    confirmBtnRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); close(false) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pending, close])

  const value = useMemo(() => confirm, [confirm])

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && typeof document !== 'undefined' &&
        createPortal(
          <div
            className="nui-confirm-scrim"
            onClick={(e) => { if (e.target === e.currentTarget) close(false) }}
          >
            <div
              className="nui-confirm"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="nui-confirm-title"
              aria-describedby={pending.message ? 'nui-confirm-msg' : undefined}
            >
              <h2 id="nui-confirm-title" className="nui-confirm__title">{pending.title}</h2>
              {pending.message && (
                <p id="nui-confirm-msg" className="nui-confirm__msg">{pending.message}</p>
              )}
              <div className="nui-confirm__foot">
                <button
                  type="button"
                  className="adm-btn"
                  onClick={() => close(false)}
                >
                  {pending.cancelLabel ?? 'Cancelar'}
                </button>
                <button
                  ref={confirmBtnRef}
                  type="button"
                  className={`adm-btn ${pending.tone === 'danger' ? 'adm-btn--danger' : 'adm-btn--primary'}`}
                  onClick={() => close(true)}
                >
                  {pending.confirmLabel ?? 'Confirmar'}
                </button>
              </div>
              <style>{`
                .nui-confirm-scrim {
                  position: fixed;
                  inset: 0;
                  z-index: var(--pos-z-modal, 100);
                  background: var(--pos-overlay);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: var(--pos-space-4, 16px);
                  animation: nui-confirm-fade var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease) both;
                }
                .nui-confirm {
                  width: min(420px, 100%);
                  background: var(--pos-surface);
                  border: 1px solid var(--pos-separator-strong);
                  border-radius: var(--pos-radius-xl, 16px);
                  box-shadow: var(--pos-shadow-4);
                  padding: var(--pos-space-5, 20px);
                  display: flex;
                  flex-direction: column;
                  gap: var(--pos-space-2, 8px);
                  animation: nui-confirm-pop var(--pos-duration-base, 200ms) var(--pos-ease-out, ease) both;
                }
                .nui-confirm__title {
                  font-family: var(--font-display, var(--font-sans));
                  font-size: var(--pos-text-lg, 17px);
                  font-weight: var(--pos-weight-semibold, 600);
                  color: var(--pos-fg);
                  margin: 0;
                }
                .nui-confirm__msg {
                  font-size: var(--pos-text-sm, 13px);
                  color: var(--pos-fg-secondary);
                  line-height: var(--pos-leading-normal, 1.45);
                  margin: 0;
                }
                .nui-confirm__foot {
                  display: flex;
                  gap: var(--pos-space-2, 8px);
                  justify-content: flex-end;
                  margin-top: var(--pos-space-3, 12px);
                }
                @keyframes nui-confirm-fade {
                  from { opacity: 0; } to { opacity: 1; }
                }
                @keyframes nui-confirm-pop {
                  from { opacity: 0; transform: scale(.96); }
                  to { opacity: 1; transform: scale(1); }
                }
                @media (prefers-reduced-motion: reduce) {
                  .nui-confirm-scrim, .nui-confirm { animation: none; }
                }
              `}</style>
            </div>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  )
}

/** Hook de confirmación. Debe usarse dentro de <ConfirmProvider>. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>')
  return ctx
}
